/**
 * TeenPattiCards — Card display with suit colors, face-down backs, animations.
 */
import React from 'react';
import { TeenPattiCardsProps, Card } from '../types';
import './TeenPattiCards.css';

const suitSymbols: Record<string, string> = {
  hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠',
};

const suitColors: Record<string, string> = {
  hearts: 'red', diamonds: 'red', clubs: 'black', spades: 'black',
};

const SingleCard: React.FC<{ card: Card | null; faceDown?: boolean; size?: string; highlighted?: boolean; isBlind?: boolean; animateReveal?: boolean }> = ({
  card, faceDown, size = 'medium', highlighted, isBlind, animateReveal,
}) => {
  const sizeClass = `tp-card--${size}`;
  if (faceDown || !card) {
    return (
      <div
        className={`tp-card tp-card--face-down ${sizeClass} ${isBlind ? 'tp-card--blind' : ''}`}
        aria-label={isBlind ? 'Card face down (Blind)' : 'Card face down'}
        role="img"
      >
        <div className="tp-card__back">
          {isBlind ? '🔒' : '🂠'}
        </div>
      </div>
    );
  }
  const color = suitColors[card.suit];
  const symbol = suitSymbols[card.suit];
  return (
    <div
      className={`tp-card tp-card--face-up ${sizeClass} ${highlighted ? 'tp-card--highlighted' : ''} ${animateReveal ? 'tp-card--reveal' : ''} tp-card--${color}`}
      aria-label={`${card.rank} of ${card.suit}`}
      role="img"
    >
      <div className="tp-card__rank">{card.rank}</div>
      <div className="tp-card__suit">{symbol}</div>
    </div>
  );
};

const TeenPattiCards: React.FC<TeenPattiCardsProps> = ({
  cards, size = 'medium', faceDown = false, animateReveal = false,
  highlighted = false, isBlind = false,
}) => {
  return (
    <div className="tp-cards" role="group" aria-label="Player cards">
      {cards ? cards.map((card, i) => (
        <SingleCard
          key={`${card.rank}-${card.suit}`}
          card={faceDown ? null : card}
          faceDown={faceDown}
          size={size}
          highlighted={highlighted}
          isBlind={isBlind}
          animateReveal={animateReveal}
        />
      )) : [0, 1, 2].map(i => (
        <SingleCard key={i} card={null} faceDown size={size} isBlind={isBlind} />
      ))}
    </div>
  );
};

export { TeenPattiCards };
export default TeenPattiCards;
